//@ts-check
import ky from 'ky';
import { getfetchopts } from '$lib/utils/utils.js';

const basePath = import.meta.env.VITE_API_URL || '';

export async function getEvents(limit = 20) {
  const fetchopts = await getfetchopts({ searchParams: { limit } });
  return ky.get(`${basePath}/api/content/events`, fetchopts).json();
}

export async function getEvent(id) {
  const fetchopts = await getfetchopts();
  return ky.get(`${basePath}/api/content/events/${id}`, fetchopts).json();
}

export async function getArticles({ limit = 20, category } = {}) {
  const searchParams = { limit };
  if (category) searchParams.category = category;
  const fetchopts = await getfetchopts({ searchParams });
  return ky.get(`${basePath}/api/content/articles`, fetchopts).json();
}

export async function getArticle(id) {
  const fetchopts = await getfetchopts();
  return ky.get(`${basePath}/api/content/articles/${id}`, fetchopts).json();
}

// ---- Auth (identity API; same-origin on the apex so the session cookie is sent) ----

/**
 * Create cross-domain login state and get the login-subdomain URL to redirect to.
 * @param {{ returnUrl?: string, device_id?: string }} args
 */
export async function createLoginState({ returnUrl = '/', device_id, localOrigin } = {}) {
  const json = { returnUrl, device_id };
  // localhost dev only: lets the API bounce the post-login callback back to the local server.
  if (localOrigin) json.localOrigin = localOrigin;
  const fetchopts = await getfetchopts({ json });
  return ky.post(`${basePath}/api/auth/create-login-state`, fetchopts).json();
}

/**
 * Exchange the one-time token from the login subdomain for the session cookie.
 * @param {{ exchange_token: string, state: string }} args
 */
export async function exchangeToken({ exchange_token, state }) {
  const fetchopts = await getfetchopts({ json: { exchange_token, state } });
  return ky.post(`${basePath}/api/auth/exchange`, fetchopts).json();
}

/**
 * Fetch the Sunday-service streaming computer credentials. Authorizer-gated and additionally
 * permission-gated server-side (view-parish-council) — returns 403 otherwise.
 * @returns {Promise<{ username: string, password: string, notes: string }>}
 */
export async function getStreamCredentials() {
  const fetchopts = await getfetchopts();
  return ky.get(`${basePath}/api/parishcouncil/streaming/credentials`, fetchopts).json();
}

// ---- Shared Utility API (generic, cross-feature; /api/util/*) ----

/**
 * Request a short-lived presigned S3 PUT URL. `purpose` (allowlisted server-side) determines the
 * required permission and the storage prefix.
 * @param {{ purpose: string, filename: string, contentType: string }} args
 * @returns {Promise<{ uploadUrl: string, key: string, imageUrl: string | null }>}
 */
export async function getUploadUrl({ purpose, filename, contentType }) {
  const fetchopts = await getfetchopts({ json: { purpose, filename, contentType } });
  return ky.post(`${basePath}/api/util/attachments/uploadurl`, fetchopts).json();
}

/**
 * Poll the status of any background task.
 * @param {string} taskId
 * @returns {Promise<{ taskstate: { status: 'pending' | 'done' | 'error', fields?: object, error?: string } }>}
 */
export async function getBgTaskStatus(taskId) {
  const fetchopts = await getfetchopts();
  return ky.get(`${basePath}/api/util/bgtask/status/${taskId}`, fetchopts).json();
}

// ---- Event creation (parish council API; permission-gated server-side with add-event) ----

/**
 * Upload image bytes directly to S3 via the presigned URL. This is a BARE request — no auth
 * cookies/hooks (getfetchopts) — and the Content-Type MUST match the type the URL was signed for.
 * @param {{ uploadUrl: string, blob: Blob, contentType: string }} args
 */
export async function putToPresignedUrl({ uploadUrl, blob, contentType }) {
  await ky.put(uploadUrl, { body: blob, headers: { 'content-type': contentType }, timeout: 60000 });
}

/**
 * Kick off AI flyer extraction; returns a task id to poll (via getBgTaskStatus).
 * @param {{ key: string }} args
 * @returns {Promise<{ taskId: string }>}
 */
export async function startAnalyzeFlyer({ key }) {
  const fetchopts = await getfetchopts({ json: { key } });
  return ky.post(`${basePath}/api/parishcouncil/events/analyze`, fetchopts).json();
}

/**
 * Create an event (single write path for both manual and AI-assisted flows).
 * @param {object} payload event fields { title, date, location, time, blurb, registrationUrl, endDate, body, imageUrl }
 * @returns {Promise<{ id: string, event: object }>}
 */
export async function createEvent(payload) {
  const fetchopts = await getfetchopts({ json: payload });
  return ky.post(`${basePath}/api/parishcouncil/events`, fetchopts).json();
}
